import { useMemo } from 'react';
import { format } from 'date-fns';
import styled from 'styled-components';

import {
  MobileSubtitle,
  RightGroup,
  SectionHeader,
  SectionTitle,
  TitleColumn,
  TitleGroup,
} from 'components/insights/careTransitions/shared/tableStyles';
import DateRangeBadge from 'components/shared/DateRangeBadge';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import SegmentedControl from 'components/shared/SegmentedControl';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useBusinessDevelopmentActions, useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';
import { Label } from 'styles/typography';

import BusinessDevelopmentApiExport from '../BusinessDevelopmentApiExport';
import {
  buildDischargesExportParams,
  buildPatientVolumeExportParams,
  buildReferralsExportParams,
  pivotSlug,
  TABLE_PIVOT,
  TABLE_PIVOT_OPTIONS,
  TablePivot,
  VIEW_BY_OPTIONS,
  viewByDimension,
  viewBySlug,
} from '../constants';

export default function BusinessDevelopmentTableHeader() {
  const viewBy = useBusinessDevelopmentStore((state) => state.viewBy);
  const tablePivot = useBusinessDevelopmentStore((state) => state.tablePivot);
  const startDate = useBusinessDevelopmentStore((state) => state.filters[START_DATE]) as Date;
  const endDate = useBusinessDevelopmentStore((state) => state.filters[END_DATE]) as Date;
  const { setTablePivot } = useBusinessDevelopmentActions();

  const overviewTitle = `${VIEW_BY_OPTIONS.find((o) => o.value === viewBy)!.label} Overview`;

  const dimension = viewByDimension(viewBy);
  const exportParams = useMemo(() => {
    switch (tablePivot) {
      case TABLE_PIVOT.DISCHARGES:
        return buildDischargesExportParams(dimension);
      case TABLE_PIVOT.REFERRALS:
        return buildReferralsExportParams(dimension);
      default:
        return buildPatientVolumeExportParams(dimension);
    }
  }, [tablePivot, dimension]);

  const exportFilename = `${viewBySlug(viewBy)}-overview-by-${pivotSlug(tablePivot)}`;

  return (
    <HeaderColumn>
      <SectionHeader>
        <TitleGroup>
          <TitleColumn>
            <SectionTitle>{overviewTitle}</SectionTitle>
            <MobileOnly>
              <MobileSubtitle>
                {format(startDate, 'MM/dd/yyyy')} - {format(endDate, 'MM/dd/yyyy')}
              </MobileSubtitle>
            </MobileOnly>
          </TitleColumn>
          <DesktopOnly>
            <DateRangeBadge startDate={startDate} endDate={endDate} />
          </DesktopOnly>
        </TitleGroup>
        <RightGroup>
          <BusinessDevelopmentApiExport params={exportParams} filename={exportFilename} />
        </RightGroup>
      </SectionHeader>
      <PivotRow>
        <PivotLabel>View By:</PivotLabel>
        <SegmentedControl
          options={[...TABLE_PIVOT_OPTIONS]}
          selectedValue={tablePivot}
          onChange={(val) => setTablePivot(val as TablePivot)}
        />
      </PivotRow>
    </HeaderColumn>
  );
}

const HeaderColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const PivotRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px 24px;
  overflow-x: auto;

  @media (max-width: 800px) {
    padding: 0 16px 24px;
  }
`;

const PivotLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black75};

  @media (max-width: 800px) {
    display: none;
  }
`;
